import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { PageHeader } from "../components/site/PageHeader";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { DishGrid } from "../components/site/DishCard";
import { formatPrice } from "../data/siteData";
import { useContent } from "../lib/content";
import { useCart } from "../lib/cart";

const MenuComplet = ({ menu }) => (
  <div className="relative h-full rounded-3xl border border-[#D4A359]/40 bg-[#123322] p-7 sm:p-10 overflow-hidden" data-testid="menu-complet">
    <div className="flex items-start justify-between gap-4">
      <div>
        <Eyebrow>À la une</Eyebrow>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl">{menu.title}</h2>
      </div>
      <span className="shrink-0 rounded-full bg-[#D4A359] px-5 py-2 font-display text-3xl text-[#0B2216]" data-testid="menu-complet-price">{formatPrice(menu.price)}</span>
    </div>
    <ol className="mt-8 space-y-4">
      {(menu.items ?? []).map((it, i) => (
        <li key={it.label} className="flex items-baseline gap-4 border-b border-[#D4A359]/15 pb-4" data-testid={`menu-complet-item-${i}`}>
          <span className="font-display italic text-[#D4A359]">0{i + 1}</span>
          <span className="text-lg text-[#F7F4EB]">{it.label}</span>
          {it.price != null && <span className="ml-auto font-semibold text-xl text-[#E5B66C]">{formatPrice(it.price)}</span>}
          {it.included && <span className="ml-auto text-sm uppercase tracking-[0.15em] text-[#E5B66C]">Inclus</span>}
        </li>
      ))}
    </ol>
    {menu.note && <p className="mt-6 text-sm text-[#F0A385]" data-testid="menu-complet-note">{menu.note}</p>}
  </div>
);

const Accent = ({ accent }) => (
  <p className="mt-6 self-start inline-flex flex-wrap items-center gap-x-2.5 sm:gap-x-3 gap-y-1 rounded-full border border-[#D4A359]/45 bg-[#D4A359]/10 px-3.5 sm:px-4 py-2" data-testid="carte-category-accent">
    <span className="h-1.5 w-1.5 rounded-full bg-[#D4A359]" aria-hidden="true" />
    <span className="text-xs uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[#A2B3A7]">{accent.label}</span>
    <span className="font-display italic text-base sm:text-lg text-[#E5B66C]">{accent.text}</span>
  </p>
);

const CategoryBlock = ({ cat }) => (
  <div className="h-full rounded-3xl border border-[#D4A359]/20 bg-[#0E2A1B] p-7 sm:p-10 flex flex-col" data-testid={`carte-category-${cat.id}`}>
    <Eyebrow>{cat.kicker}</Eyebrow>
    <h2 className="mt-4 font-display text-3xl sm:text-4xl">{cat.title}</h2>
    <ul className="mt-6 space-y-4">
      {(cat.items ?? []).map((it) => (
        <li key={it.name} className="flex items-baseline justify-between gap-4 border-b border-[#D4A359]/10 pb-4" data-testid="carte-category-item">
          <div>
            <p className="text-lg text-[#F7F4EB]">{it.name}</p>
            {it.description && <p className="text-sm text-[#A2B3A7]">{it.description}</p>}
          </div>
          {it.price != null ? (
            <span className="shrink-0 font-semibold text-[#E5B66C] text-xl">{formatPrice(it.price)}</span>
          ) : (
            <span className="shrink-0 text-xs uppercase tracking-[0.15em] text-[#A2B3A7]">Prix à confirmer</span>
          )}
        </li>
      ))}
    </ul>
    {cat.accent?.text && <Accent accent={cat.accent} />}
    {cat.note && <p className="mt-6 text-sm text-[#D6CFC0]">{cat.note}</p>}
  </div>
);

const CategoryBlocks = ({ cats }) => (
  <div className="mt-5 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="carte-categories">
    {cats.map((c, i) => (
      <Reveal key={c.id} className="h-full" delay={i * 0.08}><CategoryBlock cat={c} /></Reveal>
    ))}
  </div>
);

export default function CartePage() {
  const { content } = useContent();
  const { add } = useCart();
  const onAddPates = (p) => {
    add(p.id);
    toast.success(`Ajouté au panier : ${p.name}`);
  };
  return (
    <>
      <PageHeader testId="carte-header" eyebrow="La carte du restaurant" lines={["Notre", <em key="c" className="text-[#E5B66C]">carte</em>]} intro="La carte de CAP SUD, restaurant à Morangis : pizzas, pâtes, viandes, poissons et desserts, à déguster sur place ou à emporter." />
      {Array.isArray(content?.pates) && content.pates.length > 0 && (
        <section className="pb-20 sm:pb-28" data-testid="carte-pates-section">
          <div className="container-site">
            <Reveal className="mb-10">
              <Eyebrow>La carte</Eyebrow>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl">Pâtes</h2>
              <p className="mt-3 text-[#D6CFC0]">Au choix : spaghetti, tagliatelle ou penne.</p>
            </Reveal>
            <DishGrid items={content.pates} onAdd={onAddPates} />
          </div>
        </section>
      )}
      <section className="py-20 sm:py-28 bg-[#0E2A1B] border-t border-[#D4A359]/15">
        <div className="container-site">
          {content?.menu_complet && <Reveal><MenuComplet menu={content.menu_complet} /></Reveal>}
          {Array.isArray(content?.categories) && content.categories.length > 0 && <CategoryBlocks cats={content.categories} />}
          <div className="mt-14 flex flex-wrap gap-3">
            <Link to="/nos-pizzas" className="btn-gold" data-testid="carte-cta-pizzas">Nos pizzas <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/commander-a-emporter" className="btn-ghost btn-terracotta" data-testid="carte-cta-commander">Commander à emporter</Link>
          </div>
        </div>
      </section>
    </>
  );
}
